"use client";

import { useState, useEffect } from "react";

import { Card, CardContent } from "../UI/card";
import { Button } from "../UI/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../UI/dialog";
import JiraSidebar from "./JiraSidebar";
import {
  Clock,
  DollarSign,
  Calendar,
} from "lucide-react";

const API_BASE = import.meta.env.VITE_API_BASE_URL || '/api';

export default function TimeTrackingPage() {
  const [open, setOpen] = useState(false);

  const stats = [
    { label: "Total Hours", value: "0m", icon: Clock },
    { label: "This Week", value: "0m", icon: Calendar },
    { label: "Billable", value: "0m", icon: DollarSign },
  ];

  interface TimeLog {
    date: string;
    issue: string;
    desc: string;
    time: string;
    billable: boolean;
  }

  // State for Logs
  const [logs, setLogs] = useState<TimeLog[]>([]);

  useEffect(() => {
    fetchLogs();
  }, []);

  const fetchLogs = async () => {
    try {
      const response = await fetch(`${API_BASE}/get_time_logs.php`);
      if (response.ok) {
        const data = await response.json();
        setLogs(data);
      }
    } catch (error) {
      console.error("Error fetching logs:", error);
    }
  };

  // Form State
  const [form, setForm] = useState({
    issue: "",
    time: "",
    desc: "",
  });

  const handleLogTime = async () => {
    if (!form.issue || !form.time || !form.desc) return;

    const newLog = {
      date: new Date().toISOString().split("T")[0],
      issue: form.issue,
      desc: form.desc,
      time: form.time,
      billable: true,
    };

    try {
      const response = await fetch(`${API_BASE}/log_time.php`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(newLog),
      });

      if (response.ok) {
        fetchLogs(); // Refresh logs
        setForm({ issue: "", time: "", desc: "" });
        setOpen(false);
      } else {
        alert("Failed to save log");
      }
    } catch (error) {
      console.error("Error saving log:", error);
      alert("Error saving log");
    }
  };

  return (
    <div className="flex flex-1 bg-gray-50">
      {/* ================= DASHBOARD SIDEBAR ================= */}
      {/* ================= DASHBOARD SIDEBAR ================= */}
      <JiraSidebar activeItem="time" />

      {/* ================= MAIN CONTENT ================= */}
      <main className="flex-1 p-6 space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <h1 className="text-2xl font-semibold">Time Tracking</h1>
          <Button
            className="bg-green-600 hover:bg-green-700"
            onClick={() => setOpen(true)}
          >
            Log Time
          </Button>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {stats.map((s, i) => (
            <Card key={i} className="rounded-2xl shadow-sm">
              <CardContent className="p-6 flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">{s.label}</p>
                  <p className="text-2xl font-bold">{s.value}</p>
                </div>
                <s.icon className="w-6 h-6 text-green-600" />
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Table */}
        <Card className="rounded-2xl shadow-sm">
          <CardContent className="p-6">
            <h2 className="text-lg font-semibold mb-4">
              Recent Time Logs
            </h2>

            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-muted-foreground border-b">
                  <th className="py-2">Date</th>
                  <th>Issue</th>
                  <th>Description</th>
                  <th>Time</th>
                  <th>Billable</th>
                </tr>
              </thead>
              <tbody>
                {logs.map((l, i) => (
                  <tr key={i} className="border-b last:border-none">
                    <td className="py-2">{l.date}</td>
                    <td>{l.issue}</td>
                    <td>{l.desc}</td>
                    <td>{l.time}</td>
                    <td>
                      {l.billable ? (
                        <span className="text-green-600 font-medium">
                          Yes
                        </span>
                      ) : (
                        <span className="text-red-500 font-medium">
                          No
                        </span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </CardContent>
        </Card>
      </main>

      {/* ================= LOG TIME MODAL ================= */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Log Work Time</DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium">Issue</label>
              <select
                className="w-full mt-1 border rounded-md px-3 py-2 text-sm"
                value={form.issue}
                onChange={(e) => setForm({ ...form, issue: e.target.value })}
              >
                <option value="">Select issue</option>
                <option value="TECH-101">TECH-101</option>
                <option value="TECH-102">TECH-102</option>
                <option value="TECH-104">TECH-104</option>
              </select>
            </div>

            <div>
              <label className="text-sm font-medium">
                Time Spent
              </label>
              <input
                type="text"
                placeholder="e.g. 4h 30m"
                className="w-full mt-1 border rounded-md px-3 py-2 text-sm"
                value={form.time}
                onChange={(e) => setForm({ ...form, time: e.target.value })}
              />
            </div>

            <div>
              <label className="text-sm font-medium">
                Description
              </label>
              <textarea
                placeholder="What did you work on?"
                className="w-full mt-1 border rounded-md px-3 py-2 text-sm"
                value={form.desc}
                onChange={(e) => setForm({ ...form, desc: e.target.value })}
              />
            </div>

            <Button
              className="w-full bg-green-600 hover:bg-green-700"
              onClick={handleLogTime}
            >
              Log Time
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

/* ================= SIDEBAR ITEM ================= */

